import React, { useState, useRef, useEffect } from 'react';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import Chart from 'chart.js/auto';
import { useTranslation } from 'react-i18next';
import { useLocale, fmtDate, fmtNumber } from '../i18n/format';
import ExportButton from '../components/ExportButton';
import { buildPeakDoc } from "../pdf/builders/peak/buildDoc";
import { buildPeakWorkbook } from "../excel/builders/peak/buildWorkbook";

export default function PeakServicePage() {
  const [period, setPeriod] = useState('day');
  const [date1, setDate1] = useState('');
  const [date2, setDate2] = useState('');
  const [data, setData] = useState(null);
  const [peakInfo, setPeakInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { isAuth } = useAuth();
  const { t } = useTranslation('common');
  const locale = useLocale();

  // При изменении периода или радио — сброс дат и данных, автоматический fetch
  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    setDate1(todayStr);
    setDate2(todayStr);
    setData(null);
    setPeakInfo('');
  }, [period, radioId]);

  // Данные загружаются вручную по кнопке

  const fetchData = async () => {
    if (!isAuth || !radioId) {
      setData(null);
      setPeakInfo('');
      return;
    }
    setLoading(true);
    setPeakInfo('');
    setData(null);
    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    let url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/peak?period=${period}`;
    if (period === 'day' || period === 'month') url += `&date1=${date1}`;
    if (period === 'week') {
      const dt2 = new Date(date2);
      const dt1 = new Date(dt2);
      dt1.setDate(dt2.getDate() - 6);
      const date1str = dt1.toISOString().slice(0, 10);
      url += `&date1=${date1str}&date2=${date2}`;
    }
    try {
      const r = await authFetch(url);
      const json = await r.json();
      setData(json);
      if (json.peak && json.peak.max) {
        const v = fmtNumber(json.peak.max, locale);
        if (json.peak.dt) {
          const dt = fmtDate(json.peak.dt) || json.peak.dt;
          setPeakInfo(t('peak.peak_with_dt', { value: v, label: json.peak.label, dt }));
        } else {
          setPeakInfo(t('peak.peak', { value: v, label: json.peak.label }));
        }
      } else {
        setPeakInfo(t('peak.no_data'));
      }
      setTimeout(() => drawChart(json), 50);
    } catch {
      setPeakInfo(t('peak.request_error'));
    }
    setLoading(false);
  };

  function drawChart(resp) {
    if (!Chart || !resp.data || !chartRef.current) return;
    const ctx = chartRef.current.getContext('2d');
    if (chartInstance.current) chartInstance.current.destroy();
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: resp.labels || [],
        datasets: [{ label: t('peak.chart_label'), data: resp.data, tension: 0.25, borderWidth: 3 }]
      },
      options: {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true } }
      }
    });
  }

  // ► Пересобираем строку пика при смене языка/локали
  useEffect(() => {
    if (!data?.peak) return;
    const { max, dt, label } = data.peak || {};
    if (max) {
      const v = fmtNumber(max, locale);
      if (dt) {
        const dts = fmtDate(dt) || dt;
        setPeakInfo(t('peak.peak_with_dt', { value: v, label, dt: dts }));
      } else {
        setPeakInfo(t('peak.peak', { value: v, label }));
      }
    } else {
      setPeakInfo(t('peak.no_data'));
    }
  }, [data, t, locale]); // при смене языка t/locale строка обновится


  // Кнопки смены периода и date picker
  const renderPeriodControls = () => {
    const exportDisabled = !data?.data?.length || loading;
    const exportButtonStyle = calcExportButtonStyle(exportDisabled);
    const filenameBase = buildPeakFilename(shortcode, period, date1, date2);

    return (
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: PERIOD_BUTTON_GAP
          }}
        >
          <div style={{ display: 'inline-flex', gap: PERIOD_BUTTON_GAP }}>
            <button
              onClick={() => setPeriod('day')}
              style={periodBtn(period === 'day')}
            >{t('peak.day')}</button>
            <button
              onClick={() => setPeriod('week')}
              style={periodBtn(period === 'week')}
            >{t('peak.week')}</button>
            <button
              onClick={() => setPeriod('month')}
              style={periodBtn(period === 'month')}
            >{t('peak.month')}</button>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: DATE_FIELD_GAP,
              marginLeft: BUTTONS_DATE_SPACING,
              flexWrap: 'nowrap',
              flexShrink: 0,
              minWidth: DATE_BLOCK_MIN_WIDTH
            }}
          >
            <span style={{ minWidth: DATE_LABEL_MIN_WIDTH, fontWeight: 500, textAlign: 'right' }}>
              {period === 'week'
                ? t('peak.end_of_week')
                : period === 'month'
                ? t('peak.end_of_period')
                : t('peak.date')}
            </span>
            {period === 'day' && (
              <input
                type="date"
                value={date1}
                onChange={e => setDate1(e.target.value)}
                style={inputStyle}
              />
            )}
            {period === 'week' && (
              <input
                type="date"
                value={date2}
                onChange={e => setDate2(e.target.value)}
                style={inputStyle}
              />
            )}
            {period === 'month' && (
              <input
                type="date"
                value={date1}
                onChange={e => setDate1(e.target.value)}
                style={inputStyle}
              />
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: ACTION_BUTTON_GAP,
            marginTop: ACTION_BUTTON_GAP,
            width: '100%'
          }}
        >
          <button
            type="button"
            onClick={fetchData}
            style={showButtonStyle}
          >
            {t('common.show')}
          </button>
          <ExportButton
            data={data}
            onBeforeBuild={async () => {
              // логотип слева вверху PDF
              const url = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
              try {
                const resp = await fetch(url, { cache: 'no-store' });
                const blob = await resp.blob();
                const reader = new FileReader();
                const dataUrl = await new Promise((res, rej) => {
                  reader.onloadend = () => res(reader.result);
                  reader.onerror = rej;
                  reader.readAsDataURL(blob);
                });
                return {
                  logoDataUrl: dataUrl,
                  chartDataUrl: (chartRef.current && chartRef.current.toDataURL && chartRef.current.toDataURL("image/png")) || null
                };
              } catch {
                return {
                  logoDataUrl: null,
                  chartDataUrl: (chartRef.current && chartRef.current.toDataURL && chartRef.current.toDataURL("image/png")) || null
                };
              }
            }}
            buildDoc={(payload) =>
              buildPeakDoc({
                t, shortcode, stationName,
                serviceName: t('peak.title'),
                period, date1, date2,
                peakInfo,
                fmtNumber, locale,
                logoDataUrl: payload?.logoDataUrl || null,
                chartDataUrl: payload?.chartDataUrl || null
              })
            }
            buildWorkbook={(XLSX) =>
              buildPeakWorkbook(XLSX, {
                t, shortcode, stationName,
                serviceName: t('peak.title'),
                period, date1, date2,
                reportData: data || {},
                peakInfo
              })
            }
            filename={`${filenameBase}.pdf`}
            xlsxFilename={`${filenameBase}.xlsx`}
            label={t('export_pdf')}
            labelXlsx={t('export_excel')}
            style={exportButtonStyle}
            disabled={exportDisabled}
          />
      </div>
    </div>
    );
  };

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 8 }}>{t('peak.title')}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('peak.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      <div
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          background: "#f8f8f8",
          color: "#1a1a1a",
          fontSize: 16.5,
          margin: "8px 0 20px 0",
          boxSizing: "border-box",
          whiteSpace: "pre-line",
          lineHeight: 1.65
        }}
      >
        {t('peak.lead')}
      </div>
      <h2 style={{ marginBottom: 8 }}>{t('peak.title')}</h2>
      {renderPeriodControls()}
      <canvas
        ref={chartRef}
        width={500}
        height={220}
        style={{
          width: '100%',
          height: 220,
          display: 'block',
          border: '1px solid #ddd',
          background: '#fafafa',
          margin: '12px 0',
          borderRadius: 10
        }}
      />
      <div style={{
        fontWeight: 'bold',
        marginTop: 10,
        background: '#f4f4fa',
        padding: '8px 10px',
        borderRadius: 7,
        minHeight: 38
      }}>
        {loading ? t('common.loading') : peakInfo}
      </div>
    </div>
  );
}

const widgetStyle = {
  maxWidth: 960,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const inputStyle = {
  marginRight: 8,
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130
};

const PERIOD_BUTTON_WIDTH = 100;

const basePeriodButtonStyle = {
  background: '#f4f4fa',
  color: '#222',
  border: 'none',
  padding: '7px 18px',
  borderRadius: 5,
  fontWeight: 600,
  cursor: 'pointer',
  width: PERIOD_BUTTON_WIDTH
};

const periodBtn = (active) => ({
  ...basePeriodButtonStyle,
  background: active ? '#3867fa' : basePeriodButtonStyle.background,
  color: active ? '#fff' : basePeriodButtonStyle.color
});

const ACTION_BUTTON_WIDTH = 210;
const ACTION_BUTTON_GAP = 10;

const buildPeakFilename = (shortcode, period, date1, date2) => {
  const base = `peak_${shortcode || 'station'}_${period || 'period'}_${date1 || ''}`;
  return period === 'week' ? `${base}_${date2 || ''}` : base;
};

const baseActionButtonStyle = {
  ...periodBtn(false),
  width: ACTION_BUTTON_WIDTH
};

const showButtonStyle = {
  ...baseActionButtonStyle,
  background: '#3867fa',
  color: '#fff'
};

const calcExportButtonStyle = (disabled) => ({
  ...baseActionButtonStyle,
  background: disabled ? '#9bbdfd' : '#3867fa',
  color: '#fff'
});

const PERIOD_BUTTON_GAP = 8;
const DATE_FIELD_GAP = 8;
const BUTTONS_DATE_SPACING = -20;
const DATE_BLOCK_MIN_WIDTH = 260;
const DATE_LABEL_MIN_WIDTH = 180;
